import React, { useState, useRef } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CheckCircle2, Clock, AlertCircle, MinusCircle, CreditCard, ChevronDown, ChevronUp, Mail, Play, BookOpen, Share2, Image as ImageIcon, FileText, ArrowRight, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const IN_SCOPE_ITEMS = [{
  label: 'Clear brand & creative direction',
  status: 'closed'
}, {
  label: 'Foundational brand deep dive',
  status: 'closed'
}, {
  label: 'Logo concepts refined to one confident mark',
  status: 'closed'
}, {
  label: 'Colour palette & typography direction',
  status: 'closed'
}, {
  label: 'Final logo',
  status: 'closed'
}, {
  label: 'Roll up banner design (1)',
  status: 'closed'
}, {
  label: 'Social media template design (2)',
  status: 'closed'
}, {
  label: 'A0 and A1 Board/Poster design (Adaptation) - replacing Leaflet design',
  status: 'closed'
}, {
  label: 'Email signature',
  status: 'client_pending',
  note: 'Pending client confirmation'
}, {
  label: 'Logo guidelines',
  status: 'pending',
  note: '30th June 2026'
}];
const OUT_OF_SCOPE_ITEMS = [{
  label: 'Brand logo reveal animation',
  status: 'closed'
}, {
  label: 'Linkedin Banners',
  status: 'closed'
}, {
  label: 'A4 Letter Head',
  status: 'pending',
  note: 'Delivery: Wednesday 17 June'
}, {
  label: 'Additional social media template design',
  status: 'closed'
}, {
  label: 'Integration of social media template design to Canva',
  status: 'pending',
  note: 'Completion: Wednesday 17 June'
}];
const PAID_ITEMS = [{
  label: 'Roll Up Banner Print x2',
  status: 'paid',
  note: 'Delivered 17 June'
}, {
  label: 'Booklet design and printed copies. Print Qty: 200',
  status: 'paid',
  note: 'Delivered 17 June'
}];
const MILESTONES = [{
  title: 'Initial Deliverables',
  date: '15 June',
  icon: Mail,
  status: 'Completed',
  items: ['Email signature shared with client', 'Digital booklet PDF uploaded', 'Image assets uploaded']
}, {
  title: 'Motion & Social',
  date: '16 June',
  icon: Play,
  status: 'Completed',
  items: ['Motion animation files shared with client', 'Social media posts finalised and uploaded']
}, {
  title: 'Production/Digital Assets',
  date: '17 June',
  icon: ImageIcon,
  status: 'In Progress',
  items: ['A1 Poster Adaptation shared with client', 'A4 letterhead Word template (Pending)', 'Integration of social media template into Canva (Pending)']
}, {
  title: 'Logo Guidelines',
  date: '30 June',
  icon: FileText,
  status: 'Upcoming',
  items: ['Final comprehensive logo guidelines document to be delivered']
}];
function MilestoneItem({
  milestone,
  index
}: {
  milestone: any;
  index: number;
}) {
  const isCompleted = milestone.status === 'Completed';
  return <div className="flex-shrink-0 w-[280px] md:w-[340px] relative group" data-id="element-190">
      <div className="relative space-y-4" data-id="element-191">
        {/* Date & Title */}
        <div className="space-y-1" data-id="element-192">
          <div className="flex items-center justify-between" data-id="element-193">
            <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${milestone.date === '15 June' || milestone.date === '16 June' || milestone.date === '17 June' ? 'text-black' : isCompleted ? 'text-lime' : 'text-accent'}`} data-id="element-194">
              {milestone.date}
            </span>
            <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${isCompleted ? 'bg-lime text-black' : milestone.status === 'Upcoming' || milestone.status === 'In Progress' ? 'bg-accent text-white' : 'bg-black/5 text-black/40'}`} data-id="element-195">
              {milestone.status}
            </span>
          </div>
          <h3 className="text-base font-bold text-ink tracking-tight" data-id="element-196">
            {milestone.title}
          </h3>
        </div>

        {/* Line & Dot */}
        <div className="relative h-4 flex items-center" data-id="element-197">
          <div className={`absolute left-0 w-full h-0.5 ${isCompleted ? 'bg-lime/20' : 'bg-accent/10'}`} data-id="element-198" />
          {/* Black underline for contrast */}
          <div className={`absolute left-0 h-[3px] mt-[1px] bg-black/10 transition-all duration-1000 ${milestone.status === 'Upcoming' ? 'w-0' : milestone.status === 'In Progress' ? 'w-1/2' : 'w-full'}`} data-id="element-199" />
          <div className={`absolute left-0 h-0.5 transition-all duration-1000 ${isCompleted ? 'bg-lime w-full' : 'bg-accent w-full'} ${milestone.status === 'Upcoming' ? 'w-0' : milestone.status === 'In Progress' ? 'w-1/2' : 'w-full'}`} data-id="element-200" />
          <div className={`w-3 h-3 rounded-full border-2 border-white shadow-sm z-10 transition-colors duration-500 ${isCompleted ? 'bg-lime' : 'bg-white border-accent'}`} data-id="element-201" />
        </div>

        {/* Items */}
        <ul className="space-y-2" data-id="element-202">
          {milestone.items.map((item: string, i: number) => <li key={i} className="text-[11px] leading-relaxed text-ink/60 flex items-start gap-2" data-id="element-203">
              <div className={`mt-1.5 w-1 h-1 rounded-full shrink-0 ${isCompleted ? 'bg-lime/40' : 'bg-accent/30'}`} data-id="element-204" />
              <span data-id="element-205">{item}</span>
            </li>)}
        </ul>
      </div>
    </div>;
}
export function ChecklistPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 340; // Width of one milestone item
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'closed':
        return <div className="bg-[#d2ff46] rounded-full p-0.5 shadow-sm" data-id="element-206">
            <CheckCircle2 className="w-4 h-4 text-green-800 fill-green-800/10" data-id="element-207" />
          </div>;
      case 'client_pending':
      case 'pending':
        return <Clock className="w-5 h-5 text-lime/40" data-id="element-208" />;
      case 'waived':
        return <MinusCircle className="w-5 h-5 text-ink/20" data-id="element-209" />;
      case 'paid':
        return <div className="bg-[#d2ff46] rounded-full p-0.5 shadow-sm" data-id="element-210">
            <CreditCard className="w-4 h-4 text-green-800" data-id="element-211" />
          </div>;
      default:
        return <Clock className="w-5 h-5 text-lime/40" data-id="element-212" />;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'closed':
        return <span className="text-[10px] font-bold uppercase tracking-widest text-black bg-[#d2ff46] px-2 py-1 rounded-md shadow-sm" data-id="element-213">
            Closed
          </span>;
      case 'client_pending':
        return <span className="text-[10px] font-bold uppercase tracking-widest text-red-600 bg-red-50 px-2 py-1 rounded-md" data-id="element-214">
            Pending Confirmation
          </span>;
      case 'pending':
        return <span className="text-[10px] font-bold uppercase tracking-widest text-lime/60 bg-lime/5 px-2 py-1 rounded-md" data-id="element-215">
            Pending
          </span>;
      case 'waived':
        return <span className="text-[10px] font-bold uppercase tracking-widest text-ink/30 bg-black/5 px-2 py-1 rounded-md" data-id="element-216">
            Waived
          </span>;
      case 'paid':
        return <span className="text-[10px] font-bold uppercase tracking-widest text-black bg-[#d2ff46] px-2 py-1 rounded-md shadow-sm" data-id="element-217">
            Paid
          </span>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-[#d9e2e8] p-2 md:p-4 font-sans selection:bg-lime selection:text-black" data-id="element-218">
      <div className="main-container max-w-[1600px] mx-auto bg-card rounded-[1.5rem] md:rounded-[2rem] border border-black/5 shadow-sm min-h-[calc(100vh-2rem)] flex flex-col" data-id="element-219">
        <Navigation activePath="/project-plan" data-id="element-220" />

        <main className="flex-1 px-4 md:px-16 py-8 md:py-12 space-y-12" data-id="element-221">
          <section className="max-w-4xl" data-id="element-222">
            <div className="flex items-center gap-3" data-id="element-223">
              <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-lime" data-id="element-224" />
              <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground" data-id="element-225">
                Project Status
              </span>
            </div>
            <h1 className="mt-4 font-display text-4xl md:text-7xl tracking-[-0.04em] text-ink leading-tight" data-id="element-226">
              Project <br data-id="element-227" /> Plan
              <span className="text-lime inline-block w-2 md:w-3 h-2 md:h-3 rounded-full ml-2 mb-1" data-id="element-228" />
            </h1>
            <p className="mt-4 text-base leading-[1.6] text-ink/70 font-medium max-w-2xl" data-id="element-229">
              Track the timeline, progress of brand assets, and production
              deliverables for Headway Tuition
            </p>
          </section>

          {/* Simplified Roadmap Timeline */}
          <section className="max-w-full space-y-8 overflow-hidden py-6" data-id="element-230">
            <div className="px-4 flex items-center justify-between" data-id="element-231">
              <h2 className="text-sm font-bold uppercase tracking-[0.3em] text-ink/40" data-id="element-232">
                Project Roadmap
              </h2>
              <div className="flex items-center gap-3 text-[10px] font-bold text-black uppercase tracking-widest" data-id="element-233">
                <button onClick={() => scroll('left')} className="p-1 hover:bg-black/5 rounded-full transition-colors active:scale-95" aria-label="Scroll left" data-id="element-234">
                  <ArrowLeft size={14} className="text-black" data-id="element-235" />
                </button>
                <span className="animate-pulse" data-id="element-236">
                  Scroll horizontally to explore
                </span>
                <button onClick={() => scroll('right')} className="p-1 hover:bg-black/5 rounded-full transition-colors active:scale-95" aria-label="Scroll right" data-id="element-237">
                  <ArrowRight size={14} className="text-black" data-id="element-238" />
                </button>
              </div>
            </div>

            <div className="relative" data-id="element-239">
              <div ref={scrollRef} className="flex gap-12 overflow-x-auto pb-8 pt-2 px-4 scrollbar-hide" data-id="element-240">
                {MILESTONES.map((milestone, i) => <MilestoneItem key={i} milestone={milestone} index={i} data-id="element-241" />)}
                <div className="flex-shrink-0 w-12" data-id="element-242" />
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl" data-id="element-243">
            {/* In Scope Deliverables */}
            <div className="space-y-6" data-id="element-244">
              <h2 className="text-sm font-bold uppercase tracking-widest text-ink/40 px-2" data-id="element-245">
                In Scope
              </h2>
              <div className="bg-white rounded-[1.5rem] border border-black/5 shadow-sm overflow-hidden" data-id="element-246">
                <div className="divide-y divide-black/5" data-id="element-247">
                  {IN_SCOPE_ITEMS.map((item, i) => <div key={i} className="p-5 md:p-6 flex items-start justify-between gap-4 group hover:bg-gray-50/50 transition-colors" data-id="element-248">
                      <div className="flex gap-4" data-id="element-249">
                        <div className="mt-0.5" data-id="element-250">
                          {getStatusIcon(item.status)}
                        </div>
                        <div className="space-y-1" data-id="element-251">
                          <p className={`text-sm md:text-base font-bold ${item.status === 'closed' ? 'text-ink' : 'text-ink/60'}`} data-id="element-252">
                            {item.label}
                          </p>
                          {item.note && <p className="text-[10px] md:text-xs font-medium text-red-600 uppercase tracking-wider" data-id="element-253">
                              {item.note}
                            </p>}
                        </div>
                      </div>
                      <div className="shrink-0" data-id="element-254">
                        {getStatusBadge(item.status)}
                      </div>
                    </div>)}
                </div>
              </div>
            </div>

            <div className="space-y-12" data-id="element-255">
              {/* Costed for and Paid */}
              <div className="space-y-6" data-id="element-256">
                <h2 className="text-sm font-bold uppercase tracking-widest text-ink/40 px-2" data-id="element-257">
                  Costed for and Paid
                </h2>
                <div className="bg-white rounded-[1.5rem] border border-black/5 shadow-sm overflow-hidden" data-id="element-258">
                  <div className="divide-y divide-black/5" data-id="element-259">
                    {PAID_ITEMS.map((item, i) => <div key={i} className="p-5 md:p-6 flex items-start justify-between gap-4 group hover:bg-gray-50/50 transition-colors" data-id="element-260">
                        <div className="flex gap-4" data-id="element-261">
                          <div className="mt-0.5" data-id="element-262">
                            {getStatusIcon(item.status)}
                          </div>
                          <div className="space-y-1" data-id="element-263">
                            <p className="text-sm md:text-base font-bold text-ink" data-id="element-264">
                              {item.label}
                            </p>
                            {item.note && <p className="text-[10px] md:text-xs font-medium text-red-600 uppercase tracking-wider" data-id="element-265">
                                {item.note}
                              </p>}
                          </div>
                        </div>
                        <div className="shrink-0" data-id="element-266">
                          {getStatusBadge(item.status)}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Out of Scope */}
              <div className="space-y-6" data-id="element-267">
                <h2 className="text-sm font-bold uppercase tracking-widest text-ink/40 px-2" data-id="element-268">
                  Out of Scope
                </h2>
                <div className="bg-white rounded-[1.5rem] border border-black/5 shadow-sm overflow-hidden" data-id="element-269">
                  <div className="divide-y divide-black/5" data-id="element-270">
                    {OUT_OF_SCOPE_ITEMS.map((item, i) => <div key={i} className="p-5 md:p-6 flex items-start justify-between gap-4 group hover:bg-gray-50/50 transition-colors" data-id="element-271">
                        <div className="flex gap-4" data-id="element-272">
                          <div className="mt-0.5" data-id="element-273">
                            {getStatusIcon(item.status)}
                          </div>
                          <div className="space-y-1" data-id="element-274">
                            <p className={`text-sm md:text-base font-bold ${item.status === 'waived' ? 'text-ink/40' : 'text-ink'}`} data-id="element-275">
                              {item.label}
                            </p>
                          </div>
                        </div>
                        <div className="shrink-0" data-id="element-276">
                          {getStatusBadge(item.status)}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="p-6 bg-lime/5 rounded-[1.5rem] border border-lime/10 space-y-3" data-id="element-277">
                <div className="flex items-center gap-2 text-black" data-id="element-278">
                  <AlertCircle size={16} className="text-lime" data-id="element-279" />
                  <span className="text-xs font-bold uppercase tracking-widest" data-id="element-280">
                    Note on Project Scope
                  </span>
                </div>
                <p className="text-xs text-ink/60 leading-relaxed font-medium" data-id="element-281">
                  Additional items listed under Out of Scope or Costed for and
                  Paid ensure complete brand consistency across all touchpoints.
                </p>
              </div>
            </div>
          </div>
        </main>

        <Footer data-id="element-282" />
      </div>
    </div>;
}